import moment from 'moment';
import {VNode} from 'snabbdom';
import {AttrsSchema, Component, jsx} from 'panel';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {getCurrentMoment, parseDate} from 'common/util/date';
import {emitTrackEvent} from 'common/util/dom';
import {Visibility} from 'common/components/base/stackable';
import {ButtonEl} from 'common/components/button';
import {ButtonTheme} from 'common/components/button/constants';
import {DropMenuEl} from 'common/components/drop-menu';
import {TooltipEl} from 'common/components/tooltip';
import {DateRange, PresetOption} from 'common/widgets/date-range-picker/models/date-range';
import {DATE_RANGE_TYPES} from 'common/widgets/date-range-picker/models/date-range-factory';
import {DisplayContext} from 'common/widgets/date-range-picker/models/types';
import {
  generateDateRangeTrackingProps,
  getDatePickerMode,
  getDateRangeLabel,
  isMaxHistoryFinite,
} from 'common/widgets/date-range-picker-new/util';
import {Placement} from 'common/components/tooltip/types';
import {DateRangePicker, DateRangePickerEl} from 'common/widgets/date-range-picker-new';
import {currentProjectStore} from 'common/report/stores/project-details';
import {getReportName} from 'common/report/util';
import {useStoreAndFetchOnce} from 'common/util/component-helpers';

import {renderDateRangeTooltip} from './date-range-tooltip';
import css from './index.inline.styl';

interface DateRangePresetBarState {
  selectedPresetIdx: number;
  isCustomPickerMenuOpen: boolean;
}

interface DateRangePresetBarAttrs {
  'date-range': Partial<DateRange>;
  'display-context': DisplayContext;
  'max-history-days': number;
  'project-as-of-date': string;
  'tracked-use-case': string;
  'can-clear': boolean;
  'disable-date-picker-message': string;
}

interface DateRangePresetBarJsxProps {
  attrs: DateRangePresetBarAttrs;
  on?: {
    apply?: (ev: CustomEvent) => void;
    clear?: () => void;
  };
}

export class DateRangePresetBar extends Component<DateRangePresetBarState, DateRangePresetBarJsxProps['attrs']> {
  private selectedDateRange: null | DateRange = null;
  private customPicker: null | DateRangePicker = null;

  static get attrsSchema(): AttrsSchema<DateRangePresetBarAttrs> {
    return {
      ...super.attrsSchema,
      'date-range': {type: `json`, default: null, description: `Attributes of a valid DateRange instance`},
      'display-context': {type: `string`, default: DisplayContext.FullLength},
      'max-history-days': {type: `number`, default: 0, description: `max days time menu can query into data history`},
      'project-as-of-date': {
        type: `string`,
        default: ``,
        description: `as of date for projects that do not have real time ingestion`,
      },
      'tracked-use-case': {
        type: `string`,
        description: `For tracking purposes only. Unlike "display-context" which represents a unique configuration that may be reused in multiple places, this attribute directly identifies semantic use cases for distinguishing which parts of the product this component is used in.`,
      },
      'disable-date-picker-message': {type: `string`},
      'can-clear': {type: `boolean`, description: `Exposes a control that emits a "clear" event when clicked`},
    };
  }

  get config() {
    return {
      useShadowDom: true,
      css,
      template: () => this.renderTemplate(),
      defaultState: {
        selectedPresetIdx: -1,
        isCustomPickerMenuOpen: false,
      },
    };
  }

  connectedCallback() {
    super.connectedCallback();

    useStoreAndFetchOnce(this, currentProjectStore);
  }

  public attributeChangedCallback(name: string, oldValue: string, newValue: string) {
    switch (name) {
      case `date-range`: {
        const dateRangeAttrs = JSON.parse(newValue);
        const dateRangeClass = this.getDateRangeClass();
        const dateRange = dateRangeAttrs && new dateRangeClass(dateRangeAttrs);
        if (dateRange && !dateRange.isValid()) {
          throw new Error(`"date-range" attribute contains invalid DateRange info`);
        }
        this.selectedDateRange = dateRange ?? null;

        // super.attributeChangedCallback() naturally calls this.update();
        this.state.selectedPresetIdx = this.matchSelectedDateRangeToPreset();
        break;
      }
      case `display-context`:
      case `max-history-days`: {
        // super.attributeChangedCallback() naturally calls this.update();
        this.state.selectedPresetIdx = this.matchSelectedDateRangeToPreset();
        break;
      }
    }

    super.attributeChangedCallback(name, oldValue, newValue);
  }

  private renderTemplate(): VNode {
    const presetOptions = this.getPresetOptions();
    const noPresetSelected = -1 === this.state.selectedPresetIdx;
    const hasPresets = presetOptions.length > 0;

    const customPickerDateRange = this.getCustomPickerDateRange();
    const hasDateRange = !!customPickerDateRange;
    return (
      <div sel='.root'>
        {this.attr(`disable-date-picker-message`)
          ? this.renderDisableTooltip(this.attr(`disable-date-picker-message`))
          : null}

        {/* Custom selector (opens the custom picker menu) */}
        <div class={{selected: noPresetSelected && hasPresets && hasDateRange}} attrs={{elref: `customItem`}}>
          <ButtonEl
            attrs={{
              icon: `calendar`,
              theme: ButtonTheme.Tertiary,
              pressed: this.state.isCustomPickerMenuOpen,
              disabled: !!this.attr(`disable-date-picker-message`),
              elref: `customButton`,
            }}
            on={{click: () => this.handleClickCustom()}}
          >
            {this.getDateRangeLabel(customPickerDateRange)}
          </ButtonEl>

          {hasDateRange && !this.attr(`disable-date-picker-message`) ? this.renderTooltip(customPickerDateRange) : null}
        </div>

        {presetOptions.map((option: PresetOption, idx: number) => {
          const dateRangeClass = this.getDateRangeClass();
          const dateRange = new dateRangeClass(option.value);
          if (!dateRange.isValid()) {
            throw new Error(`"${option.label}" preset is invalid`);
          }
          const maxHistoryDays = this.attr(`max-history-days`);
          const isLimited = isMaxHistoryFinite(maxHistoryDays) && !dateRange.isValid(maxHistoryDays);
          return (
            <div sel='.preset-item' class={{selected: idx === this.state.selectedPresetIdx}}>
              <ButtonEl
                attrs={{
                  theme: ButtonTheme.Tertiary,
                  elref: `presetButton`,
                  disabled: isLimited || !!this.attr(`disable-date-picker-message`),
                }}
                on={{click: () => this.handleClickPreset(option, dateRange)}}
              >
                {option.shortLabel}
              </ButtonEl>

              {isLimited ? (
                <>
                  {/* overlay necessary to cover up the "pointer-events: none" from a disabled <mp-button> that prevents tooltips from showing */}
                  <div sel='.invis-overlay' />
                  <TooltipEl attrs={{placement: Placement.Top, elref: `dateRangeTooltip`}}>
                    Your current plan supports {maxHistoryDays} days of data. Upgrade your plan to query more data.
                  </TooltipEl>
                </>
              ) : !this.attr(`disable-date-picker-message`) ? (
                this.renderTooltip(dateRange)
              ) : null}
            </div>
          );
        })}

        {this.attr(`can-clear`) ? (
          <div sel='.preset-item' class={{selected: !this.selectedDateRange}}>
            <ButtonEl
              attrs={{theme: ButtonTheme.Tertiary, elref: `clearButton`}}
              on={{click: () => this.handleClickClear()}}
            >
              {`Default`}
            </ButtonEl>
            <TooltipEl attrs={{placement: Placement.Top, elref: `dateRangeTooltip`}}>
              Date selection set by report
            </TooltipEl>
          </div>
        ) : null}

        {this.renderCustomPickerMenu(customPickerDateRange)}

        {/* we apply a border to this ".overlapping-outline" element instead of ".root" in order to avoid adding height to this widget */}
        <div sel='.overlapping-outline' />
      </div>
    );
  }

  private renderTooltip(dateRange: DateRange): VNode {
    return renderDateRangeTooltip(dateRange, currentProjectStore);
  }

  private renderDisableTooltip(disableMessage: string): VNode {
    return <TooltipEl attrs={{placement: Placement.Top, elref: `dateRangeTooltip`}}>{disableMessage}</TooltipEl>;
  }

  private renderCustomPickerMenu(dateRange: null | DateRange): VNode {
    return (
      <DropMenuEl
        sel='.custom-picker-menu'
        attrs={{open: this.state.isCustomPickerMenuOpen}}
        on={{change: (ev: CustomEvent) => this.update({isCustomPickerMenuOpen: Visibility.Open === ev.detail.state})}}
      >
        {this.state.isCustomPickerMenuOpen ? (
          <DateRangePickerEl
            attrs={{
              'display-context': this.attr(`display-context`),
              'date-range': JSON.stringify(dateRange?.getAttributes() ?? null),
              'project-as-of-date': this.attr(`project-as-of-date`),
              'max-history-days': this.attr(`max-history-days`),
              'tracked-use-case': this.attr(`tracked-use-case`) || undefined,
              'force-hide-preset-menu': true,
              elref: `dateRangePicker`,
            }}
            on={{
              apply: (ev: CustomEvent) => {
                this.dispatchEvent(new CustomEvent(`apply`, {detail: {dateRange: ev.detail.dateRange}}));
                this.update({isCustomPickerMenuOpen: false});
              },
              cancel: () => this.update({isCustomPickerMenuOpen: false}),
            }}
            hook={{
              insert: (vnode: VNode) => (this.customPicker = vnode.elm as DateRangePicker),
              remove: (_, removeCallback) => {
                this.customPicker = null;
                removeCallback();
              },
            }}
          />
        ) : null}
      </DropMenuEl>
    );
  }

  private getDateRangeClass(): typeof DateRange {
    const displayContext = this.attr(`display-context`);
    return DATE_RANGE_TYPES[displayContext];
  }

  private getProjectAsOfDate(): moment.Moment {
    const explicitAsOfDate = this.attr(`project-as-of-date`);
    if (explicitAsOfDate) {
      const parsedDate = parseDate(explicitAsOfDate);
      if (!parsedDate) {
        throw new Error(`"project-as-of-date" attribute does not contain a valid date string`);
      }
      return moment(parsedDate);
    }
    return getCurrentMoment({localized: true});
  }

  private getPresetOptions(): PresetOption[] {
    const dateRangeClass = this.getDateRangeClass();
    return dateRangeClass.getPresetOptions();
  }

  private matchSelectedDateRangeToPreset(): number {
    if (this.selectedDateRange) {
      const dateRangeClass = this.getDateRangeClass();
      return this.getPresetOptions().findIndex((option) =>
        this.selectedDateRange.equals(new dateRangeClass(option.value)),
      );
    }
    return -1;
  }

  private getCustomPickerDateRange(): null | DateRange {
    const noPresetSelected = this.state.selectedPresetIdx === -1;
    if (noPresetSelected && this.selectedDateRange) {
      const dateRangeClass = this.getDateRangeClass();
      return new dateRangeClass(this.selectedDateRange);
    }

    // if a preset is selected, we intentionally treat the custom picker as if it was empty
    return null;
  }

  private getDateRangeLabel(dateRange: null | DateRange): string {
    if (!dateRange) {
      // the getDateRangeLabel() utility method already handles nulls, but we want a different default label in this widget
      return `Custom`;
    }
    const noPresetSelected = this.state.selectedPresetIdx === -1;
    const matchingPreset = noPresetSelected ? null : this.getPresetOptions()[this.state.selectedPresetIdx];
    return getDateRangeLabel(
      dateRange,
      matchingPreset,
      null /* project as of date; we set this to "null" because we're trialing a change where we show the project-as-of-date in the tooltip, not the label */,
      getCurrentMoment({localized: true}),
    );
  }

  private handleClickCustom(): void {
    this.update({isCustomPickerMenuOpen: true});
    requestAnimationFrame(() => this.customPicker?.customScrollIntoViewIfNeeded());
  }

  private handleClickPreset(presetOption: PresetOption, dateRange: DateRange) {
    this.dispatchEvent(new CustomEvent(`apply`, {detail: {dateRange}}));

    this.track(`[mp-query-builder] Select date preset`, {
      ...generateDateRangeTrackingProps(
        dateRange,
        getReportName(),
        getDatePickerMode(dateRange),
        this.getProjectAsOfDate(),
        this.attr(`tracked-use-case`),
      ),
      'Preset name': presetOption.label,
      'Preset source': `mixpanel`, // TODO: move to an enum if/when there are additional sources for presets (eg. user defined presets)
    });
  }

  private handleClickClear() {
    this.dispatchEvent(new CustomEvent(`clear`));
  }

  private track(event: string, properties: {[index: string]: any}): void {
    emitTrackEvent(this, {eventName: event, props: properties, eventPrefix: null, cancelable: true});
  }
}

export const DateRangePresetBarEl = defineAndMakeJsxFactory<DateRangePresetBarJsxProps>(
  `mp-date-range-preset-bar`,
  DateRangePresetBar,
);
export const DateRangePresetBarReact = panel2react<DateRangePresetBarJsxProps>(
  `mp-date-range-preset-bar`,
  DateRangePresetBar,
);
